import { useToastContext } from '@rtkwlf/fenrir-react';
import type { EditScannerConfigInput, ResponseError } from '@rtkwlf/rendall';
import type { UseMutationOptions } from '@tanstack/react-query';
import { QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useEditScannerConfig } from '@apis/rendall/queries/use-edit-scanner-config/use-edit-scanner-config';
import { TOAST_DURATION } from 'ae-constants/toast';

import { useTelemetryQueryKey } from '../../../../../../components/use-query-key-factory';

type ResponseObjectData = {
  message: string;
  code: number;
};

const getResponseObjectData = async (data: Response) => {
  const reader = data.body?.getReader();

  if (data.status === 202 || !reader) {
    return { message: '', code: data.status } as ResponseObjectData;
  }

  const decoder = new TextDecoder();
  let result = '';
  let done = false;
  while (!done) {
    const { value, done: streamDone } = await reader.read();
    done = streamDone;
    if (value) {
      result += decoder.decode(value, { stream: true });
    }
  }
  if (result === '')
    return { message: '', code: data.status } as ResponseObjectData;

  return JSON.parse(result) as ResponseObjectData;
};

export const useEditScannerConfigWrapper = (
  scannerId: string,
  options?: UseMutationOptions<
    Response,
    ResponseError,
    EditScannerConfigInput,
    unknown
  >,
) => {
  const queryClient = new QueryClient();
  const { telemetryScanners } = useTelemetryQueryKey();
  const { t } = useTranslation('telemetryScannerConfig');
  const { addToast } = useToastContext();

  return useEditScannerConfig(scannerId, {
    gcTime: 0,
    onError: () => {
      addToast({
        appearance: 'danger',
        duration: TOAST_DURATION,
        message: t('notifications.edit.error'),
        title: t('notifications.error'),
      });
    },
    onSuccess: async (data, _variable, _context) => {
      queryClient.invalidateQueries({
        queryKey: telemetryScanners.detail(scannerId),
      });

      const responseObjectData = await getResponseObjectData(data);
      addToast({
        appearance: responseObjectData.code === 202 ? 'success' : 'warning',
        duration: TOAST_DURATION,
        message:
          responseObjectData.code === 202
            ? t('notifications.edit.success')
            : responseObjectData.message ===
                'scanner config update failed on IVA'
              ? t('notifications.edit.warningIva')
              : responseObjectData.message ===
                  'scanner config update failed on CD'
                ? t('notifications.edit.warningCD')
                : '',
        title:
          responseObjectData.code === 202
            ? t('notifications.success')
            : t('notifications.warning'),
      });
    },
    ...options,
  });
};
